import { zeroErrorMessage } from "@/lib/zero-error";
import { useMemo, useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export type ProjectGroup = Tables<"project_groups">;
export type ProjectGroupInsert = TablesInsert<"project_groups">;
export type ProjectGroupUpdate = TablesUpdate<"project_groups">;

export type ProjectGroupWithRelations = ProjectGroup & {
  clockify_project_id?: string | null;
  clockify_synced_at?: string | null;
  companies: { name: string } | null;
  contact: { first_name: string; last_name: string } | null;
  projects: {
    id: string;
    name?: string;
    number?: string | null;
    status?: string | null;
    event_start_date?: string | null;
    event_end_date?: string | null;
    deleted_at?: string | null;
  }[];
};

type ZeroProjectGroupRow = {
  id: string;
  name: string;
  number?: string | null;
  customerID?: string | null;
  contactID?: string | null;
  description?: string | null;
  status: "active" | "archived";
  color?: string | null;
  clockifyProjectID?: string | null;
  clockifySyncedAt?: number | string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  deletedAt?: number | string | null;
  version?: number | null;
  companies?: { name: string } | null;
  contact?: { firstName: string; lastName: string } | null;
  projects?: {
    id: string;
    name?: string;
    number?: string | null;
    status?: string | null;
    eventStartDate?: number | string | null;
    eventEndDate?: number | string | null;
    deletedAt?: number | string | null;
  }[];
};

type MutationOptions<T> = { onSuccess?: (data: T) => void };
type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyNullableTimestamp(value: number | string | null | undefined) {
  return value ? toLegacyTimestamp(value) : null;
}

function toLegacyProjectGroup(
  row: ZeroProjectGroupRow,
): ProjectGroupWithRelations {
  return {
    id: row.id,
    name: row.name,
    number: row.number ?? null,
    customer_id: row.customerID ?? null,
    contact_id: row.contactID ?? null,
    description: row.description ?? null,
    status: row.status,
    color: row.color ?? null,
    clockify_project_id: row.clockifyProjectID ?? null,
    clockify_synced_at: toLegacyNullableTimestamp(row.clockifySyncedAt),
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    deleted_at: toLegacyNullableTimestamp(row.deletedAt),
    version: row.version ?? null,
    companies: row.companies ? { name: row.companies.name } : null,
    contact: row.contact
      ? {
          first_name: row.contact.firstName,
          last_name: row.contact.lastName,
        }
      : null,
    projects: (row.projects ?? []).map((project) => ({
      id: project.id,
      name: project.name,
      number: project.number ?? null,
      status: project.status ?? null,
      event_start_date: toLegacyNullableTimestamp(project.eventStartDate),
      event_end_date: toLegacyNullableTimestamp(project.eventEndDate),
      deleted_at: toLegacyNullableTimestamp(project.deletedAt),
    })),
  };
}

function toZeroProjectGroupInput(group: ProjectGroupInsert, id: string) {
  return {
    id,
    name: group.name,
    number: group.number ?? null,
    customerID: group.customer_id ?? null,
    contactID: group.contact_id ?? null,
    description: group.description ?? null,
    status: group.status ?? "active",
    color: group.color ?? null,
  };
}

function toZeroProjectGroupUpdate(id: string, updates: ProjectGroupUpdate) {
  return {
    id,
    ...(updates.name !== undefined ? { name: updates.name } : {}),
    ...(updates.number !== undefined ? { number: updates.number } : {}),
    ...(updates.customer_id !== undefined
      ? { customerID: updates.customer_id }
      : {}),
    ...(updates.contact_id !== undefined
      ? { contactID: updates.contact_id }
      : {}),
    ...(updates.description !== undefined
      ? { description: updates.description }
      : {}),
    ...(updates.status !== undefined ? { status: updates.status } : {}),
    ...(updates.color !== undefined ? { color: updates.color } : {}),
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useProjectGroups(includeDeleted = false) {
  const [data, details] = useQuery(
    appQueries.projectGroups.list({ includeDeleted }),
  );
  const groups = useMemo(
    () => ((data ?? []) as ZeroProjectGroupRow[]).map(toLegacyProjectGroup),
    [data],
  );

  return {
    data: groups,
    isLoading: details.type !== "complete" && groups.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useProjectGroup(id: string | undefined) {
  const query = id ? appQueries.projectGroups.byID({ id }) : undefined;
  const [data, details] = useQuery(query, { enabled: !!id });

  return {
    data: data ? toLegacyProjectGroup(data as ZeroProjectGroupRow) : null,
    isLoading: !!id && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateProjectGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (group: ProjectGroupInsert) => {
    const input = toZeroProjectGroupInput(group, generateUUID());
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.projectGroups.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project group create was rejected",
      );
      return toLegacyProjectGroup({ ...input, createdAt: "", updatedAt: "" });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      group: ProjectGroupInsert,
      options?: MutationOptions<ProjectGroupWithRelations>,
    ) => {
      void run(group)
        .then((data) => {
          toast.success("Project group created successfully");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to create project group: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateProjectGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: ProjectGroupUpdate;
  }) => {
    const input = toZeroProjectGroupUpdate(id, updates);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.projectGroups.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project group update was rejected",
      );
      return toLegacyProjectGroup({
        ...input,
        name: input.name ?? "",
        status: input.status ?? "active",
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; updates: ProjectGroupUpdate },
      options?: MutationOptions<ProjectGroupWithRelations>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Project group updated successfully");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to update project group: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useSoftDeleteProjectGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectGroups.softDelete({ id }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project group delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Project group moved to trash");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete project group: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useRestoreProjectGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.projectGroups.restore({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project group restore was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Project group restored");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to restore project group: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
